import { useMutation } from '@apollo/client'
import { Transition } from '@headlessui/react'
import { useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'

import { AddAccountPaths } from '../../../config/routes'
import { Notify } from '../../../lib/helpers'
import { Button, Spinner } from '../..'
import { ErrorIcon, SuccessIcon } from '../../toast-notification/icons'
import { StoreCredential } from '../queries'

export interface SyncProps {
    onClick: () => void
    show: boolean
    code: string | string[] | null
    state: string | string[] | null
    finished: boolean
}

export function Sync({
    onClick,
    show,
    code,
    state,
    finished,
}: SyncProps): JSX.Element {
    const institution = useMemo(() => {
        if (typeof state === 'string') {
            return state.split('-')[1]
        }
    }, [state])

    const [storeCode, { data, loading }] = useMutation(StoreCredential, {
        variables: { code, institution },
        errorPolicy: 'ignore',
    })

    useEffect(() => {
        if (code && institution) {
            storeCode()
        }
    }, [code, institution, storeCode])

    const success = useMemo(() => {
        return !!data?.storeCredential
    }, [data])

    useEffect(() => {
        if (!finished && success) {
            Notify(
                'success',
                'Yippee!',
                'Your account has been connected through Vezgo',
            )

            onClick()
        }
    }, [success, finished, onClick])

    const message = useMemo(() => {
        if (loading)
            return (
                <p className='text-xl text-center text-white'>
                    <b>Masa</b> is syncing with <b>Vezgo</b>
                </p>
            )

        if (data?.storeCredential) {
            return (
                <p className='text-xl text-center text-white'>
                    <b>Synced!</b>
                </p>
            )
        } else {
            return (
                <p className='text-xl text-center text-white'>
                    <b>Masa</b> could not sync with <b>Vezgo</b>
                </p>
            )
        }
    }, [loading, data])

    return (
        <Transition
            appear={true}
            show={show}
            className='flex flex-col justify-between px-8 py-8'
            style={{ minHeight: 300 }}
            as='div'
            enter='transition ease-in-out duration-300 delay-600 transform opacity-0'
            enterFrom='translate-x-full opacity-0'
            enterTo='translate-x-0 opacity-100'
            leave='transition ease-in-out duration-300 transform opacity-100 absolute'
            leaveFrom='translate-x-0 opacity-100'
            leaveTo='-translate-x-full opacity-0'>
            {message}

            <div className='flex flex-row justify-center w-full'>
                {loading ? (
                    <Spinner color='black' size='large' />
                ) : data?.storeCredential ? (
                    <SuccessIcon />
                ) : (
                    <ErrorIcon />
                )}
            </div>

            <Link to={AddAccountPaths.ADD_COINBASE_ACCOUNT}>
                <Button
                    fill
                    loading={loading}
                    onClick={onClick}
                    style={{ background: 'white' }}
                    className='px-32 mt-12 text-black lg:w-full w-max lg:mt-0'>
                    {loading ? 'Syncing...' : 'Continue'}
                </Button>
            </Link>
        </Transition>
    )
}
